/**
 * 일정 만들기·수정 창의 «새 부품» 글귀 — 반복 설정 4탭 · 팀원 모두 초대 (2026-10-13 이식).
 *
 * ⚠ 왜 `calendar-i18n.ts` 에 안 넣었나: 그 파일은 여러 세션이 동시에 고치는 큰 사전이다(700줄).
 *   새 부품의 글귀를 이 파일에 모아 두면 그쪽과 줄이 안 부딪친다. 규칙(언어 사슬 · {param} 치환)은 같다.
 * ⚠ 서버 오류 코드의 글귀(`calendar.error.code.*`)는 `describeCalendarError` 가 그 사전에서만 찾으므로
 *   그쪽에 둔다 — 여기는 «화면 부품»의 글귀뿐이다.
 */
import { getSharedLang, FALLBACK_LANG, type Lang } from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ── 반복 4탭 (옛 가지 ee33d27d7) ──
  'rc.tab_daily': { ko: '매일', en: 'Daily', ja: '毎日' },
  'rc.tab_weekly': { ko: '매주', en: 'Weekly', ja: '毎週' },
  'rc.tab_monthly': { ko: '매월', en: 'Monthly', ja: '毎月' },
  'rc.tab_yearly': { ko: '매년', en: 'Yearly', ja: '毎年' },
  'rc.unit_daily': { ko: '일마다', en: 'day(s)', ja: '日ごと' },
  'rc.unit_weekly': { ko: '주마다', en: 'week(s)', ja: '週ごと' },
  'rc.unit_monthly': { ko: '개월마다', en: 'month(s)', ja: 'か月ごと' },
  'rc.unit_yearly': { ko: '년마다', en: 'year(s)', ja: '年ごと' },
  // ⭐ 2026-10-14 (반복 규칙 E-3) — 종료 「기간」의 단위(「10주 동안」).
  'rc.period_unit_daily': { ko: '일 동안', en: 'day(s)', ja: '日間' },
  'rc.period_unit_weekly': { ko: '주 동안', en: 'week(s)', ja: '週間' },
  'rc.period_unit_monthly': { ko: '개월 동안', en: 'month(s)', ja: 'か月間' },
  'rc.period_unit_yearly': { ko: '년 동안', en: 'year(s)', ja: '年間' },
  'rc.freq_label': { ko: '반복 주기', en: 'Frequency', ja: '繰り返し周期' },
  'rc.weekdays_label': { ko: '반복 요일', en: 'Repeat on', ja: '繰り返す曜日' },
  'rc.mode_label': { ko: '반복 방식', en: 'Repeat by', ja: '繰り返し方法' },
  'rc.weekday.sun': { ko: '일', en: 'Su', ja: '日' },
  'rc.weekday.mon': { ko: '월', en: 'Mo', ja: '月' },
  'rc.weekday.tue': { ko: '화', en: 'Tu', ja: '火' },
  'rc.weekday.wed': { ko: '수', en: 'We', ja: '水' },
  'rc.weekday.thu': { ko: '목', en: 'Th', ja: '木' },
  'rc.weekday.fri': { ko: '금', en: 'Fr', ja: '金' },
  'rc.weekday.sat': { ko: '토', en: 'Sa', ja: '土' },
  'rc.nth.1': { ko: '첫째', en: 'first', ja: '第1' },
  'rc.nth.2': { ko: '둘째', en: 'second', ja: '第2' },
  'rc.nth.3': { ko: '셋째', en: 'third', ja: '第3' },
  'rc.nth.4': { ko: '넷째', en: 'fourth', ja: '第4' },
  // ── 매월·매년의 두 방식 (옛 가지 21f8b4e23 · 말일 ec36d8a56) ──
  'rc.monthly_by_date': { ko: '매월 {day}일', en: 'Monthly on day {day}', ja: '毎月{day}日' },
  'rc.monthly_by_date_clamp': { ko: '매월 {day}일 (없는 달은 말일)', en: 'Monthly on day {day} (last day if shorter)', ja: '毎月{day}日（ない月は末日）' },
  'rc.monthly_last_day': { ko: '매월 말일', en: 'Monthly on the last day', ja: '毎月末日' },
  'rc.monthly_by_weekday': { ko: '매월 {nth} {weekday}요일', en: 'Monthly on the {nth} {weekday}', ja: '毎月{nth}{weekday}曜日' },
  'rc.monthly_by_weekday_last': { ko: '매월 마지막 {weekday}요일', en: 'Monthly on the last {weekday}', ja: '毎月最終{weekday}曜日' },
  'rc.yearly_by_date': { ko: '매년 {month}월 {day}일', en: 'Yearly on {month}/{day}', ja: '毎年{month}月{day}日' },
  'rc.yearly_feb29': { ko: '매년 2월 29일 (평년은 2월 28일)', en: 'Yearly on Feb 29 (Feb 28 in common years)', ja: '毎年2月29日（平年は2月28日）' },
  'rc.yearly_by_weekday': { ko: '매년 {month}월 {nth} {weekday}요일', en: 'Yearly on the {nth} {weekday} of month {month}', ja: '毎年{month}月{nth}{weekday}曜日' },
  'rc.yearly_by_weekday_last': { ko: '매년 {month}월 마지막 {weekday}요일', en: 'Yearly on the last {weekday} of month {month}', ja: '毎年{month}月最終{weekday}曜日' },
  'rc.weekly_needs_day': { ko: '요일을 하나 이상 골라 주세요.', en: 'Pick at least one day.', ja: '曜日を1つ以上選んでください。' },
  // ── 수정 화면 (옛 가지 e9255f050 · 6dba1d0ad) ──
  // ⭐ 2026-10-14 (반복 규칙 F) — 「전체 일정」은 지난 일정은 그대로 두고 오늘 이후부터(종전 E-4: 기존 일정이 남을 때만).
  'rc.edit_future_hint': { ko: '반복 설정을 바꾸면 ‘이후 일정’은 이 회차부터, ‘전체 일정’은 지난 일정은 그대로 두고 오늘 이후 일정부터 적용됩니다.', en: 'Changing the repeat settings applies from this occurrence for “This and following events”, and from today on for “All events” (past events stay).', ja: '繰り返し設定の変更は「これ以降の予定」ではこの回から、「すべての予定」では過去の予定をそのままにして今日以降の予定から適用されます。' },
  // ⭐ 2026-10-14 반복 규칙 1단계(B) — 범위 창 「이후 일정」 미리보기. {ro} 는 한국어 조사(으로/로).
  'rc.preview_future': { ko: '이후 일정은 {rule}{ro} 반복됩니다.', en: 'Following events will repeat {rule}.', ja: 'これ以降の予定は{rule}で繰り返します。' },
  'rc.every_n_days': { ko: '{n}일마다', en: 'every {n} days', ja: '{n}日ごと' },
  'rc.weekly_on': { ko: '매주 {days}', en: 'weekly on {days}', ja: '毎週{days}' },
  'rc.every_n_weeks_on': { ko: '{n}주마다 {days}', en: 'every {n} weeks on {days}', ja: '{n}週ごとに{days}' },
  'rc.every_n_months_suffix': { ko: '{rule}({n}개월마다)', en: '{rule} (every {n} months)', ja: '{rule}（{n}か月ごと）' },
  'rc.every_n_years_suffix': { ko: '{rule}({n}년마다)', en: '{rule} (every {n} years)', ja: '{rule}（{n}年ごと）' },
  'rc.date_rule_follows_hint': { ko: '날짜를 옮기면 반복 방식도 새 날짜에 맞춰 바뀝니다(이후 일정 모두).', en: 'Moving the date updates the repeat pattern to the new date (this and following events).', ja: '日付を移すと繰り返し方法も新しい日付に合わせて変わります（これ以降の予定）。' },
  // ⭐ 2026-10-14 (반복 규칙 G) — 「전체 일정」 내용 수정이 '이 일정만' 수정 칸과 겹칠 때(recurrence-override-conflict.ts). 예/아니오/닫기.
  'rc.override_conflict_confirm': { ko: "기존에 '이 일정만'으로 수정한 내역이 있습니다. 해당 일정도 수정하겠습니까?", en: 'Some events were changed with “This event” before. Change those events too?', ja: '以前「この予定のみ」で変更した予定があります。その予定も変更しますか？' },
  'rc.override_conflict_yes': { ko: '예', en: 'Yes', ja: 'はい' },
  'rc.override_conflict_no': { ko: '아니오', en: 'No', ja: 'いいえ' },
  // ⭐ 2026-10-14 (반복 규칙 G 보강) — 날짜·시간·반복 변경으로 따로 고친 회차가 사라질 때 덧붙이는 안내. 아니오·닫기 = 저장 안 함.
  'rc.override_loss_note': { ko: '예를 누르면 따로 수정한 내용은 새 일정에 맞춰 사라집니다.', en: 'If you choose Yes, those separate changes will be replaced by the new schedule.', ja: '「はい」を押すと、個別に変更した内容は新しい予定に合わせて消えます。' },
  // ── 팀원 모두 초대 (옛 가지 4b3338c66 · a881fa6a5 · 9b12617fe) ──
  'invite.team_all': { ko: '{team} 팀원 모두 초대', en: 'Invite all of {team}', ja: '{team}のメンバーを全員招待' },
  'invite.busy': { ko: '팀원을 불러오는 중…', en: 'Loading members…', ja: 'メンバーを読み込み中…' },
  'invite.added': { ko: '{n}명을 참석자에 더했습니다.', en: 'Added {n} attendee(s).', ja: '{n}人を参加者に追加しました。' },
  'invite.none_new': { ko: '더할 팀원이 없습니다 — 모두 이미 참석자입니다.', en: 'No one to add — everyone is already invited.', ja: '追加するメンバーはいません（全員参加者です）。' },
  'invite.over': { ko: '참석자는 최대 {max}명이라 {n}명은 넣지 못했습니다.', en: 'Attendees are limited to {max}; {n} could not be added.', ja: '参加者は最大{max}人のため、{n}人は追加できませんでした。' },
  'invite.failed': { ko: '팀원 목록을 불러오지 못했습니다. 잠시 뒤 다시 눌러 주세요.', en: 'Couldn’t load the members. Try again shortly.', ja: 'メンバーを読み込めませんでした。しばらくしてから再度お試しください。' },
};

export type CalendarCreateI18nKey = keyof typeof MESSAGES;

/** `tCalendar` 와 같은 규칙 — lang → en → ko, `{name}` 치환. */
export function tCalCreate(key: CalendarCreateI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}
